/**
 * Helper functions for managing warehouse locations
 */

/**
 * Creates a new location section with a unique ID
 * @param {Array} currentSections - Current location sections
 * @returns {Object} - New location section object
 */
export const createNewLocationSection = (currentSections) => {
    const newLocationId = currentSections.length > 0 
      ? Math.max(...currentSections.map(s => s.element_id)) + 1 
      : 1;
      
    return { 
      element_id: newLocationId, 
      id: null, 
      supplier_product_id: 0,
      quantity: 0,
      name: '',
    };
  };
  
  /**
   * Validates all location sections for required fields
   * @param {Array} locationSections - Array of location section objects
   * @returns {Object} - Object containing errors and validation status
   */
  export const validateLocationSections = (locationSections) => {
    const newErrors = {};
    let isValid = true;
    if (locationSections.length > 1) {
      locationSections.forEach(section => {
        newErrors[section.element_id] = {};
        
        if (!section.name) {
          newErrors[section.element_id].name = 'Location Name is required';
          isValid = false;
        }
      });
    }
    return { errors: newErrors, isValid };
  };
  
  /**
   * Validates the main warehouse form fields
   * @param {Object} formData - Object containing form field values
   * @returns {Object} - Object containing errors and validation status
   */
  export const validateWarehouseForm = ({ warehouseName, warehouseSupplier, warehouseAddress }) => {
    let errors = {};
    let isValid = true;
  
    if (!warehouseName) {
      errors.warehouseName = 'Warehouse name is required';
      isValid = false;
    }
  
    if (warehouseSupplier === 0) {
      errors.warehouseSupplier = 'Supplier is required';
      isValid = false;
    }
  
    return { errors, isValid };
  };
  
  /**
   * Formats location data for API submission
   * @param {Array} locationSections - Array of location section objects
   * @returns {Array} - Formatted array for API submission
   */
  export const formatLocationsForSubmission = (locationSections) => {
    return locationSections.map(section => ({
      id: section.id,
      name: section.name,
      is_default: section.is_default || false,
      quantity: section.quantity ?? 0,
    }));
  };
  
  /**
   * Formats products for display in dropdown
   * @param {Array} products - Array of product objects
   * @returns {Array} - Array of option elements or null if no products
   */
  export const formatProductsForDropdown = (products) => {
    if (products && products.length > 0) {
      const sortedProducts = [...products].sort((a, b) => {
        const nameA = a.supplier_product_name.toLowerCase();
        const nameB = b.supplier_product_name.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  
      return sortedProducts;
    }
    return [];
  };
  
  /**
   * Updates location data with special handling for is_default field
   * Ensures only one location can be marked as default at a time
   * @param {Array} sections - Current location sections array
   * @param {number} element_id - ID of the location to update
   * @param {string} field - Field name to update
   * @param {any} value - New value for the field
   * @returns {Array} - Updated sections array
   */
  export const updateLocationData = (sections, element_id, field, value) => {
    // If setting is_default to true, set all others to false
    if (field === 'is_default' && value === true) {
      return sections.map(section => ({
        ...section,
        is_default: section.element_id === element_id
      }));
    }
    
    return sections.map(section =>
      section.element_id === element_id ? { 
        ...section, 
        [field]: value
      } : section
    );
  };